import React, { useEffect, useState } from 'react'
import { Button, EmptyState } from '@enamelai/design-system'
import { Icon as ArrowLeftIcon } from '@enamelai/design-system/icons/Arrow_2_left'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { ReportInPlaceExpandProvider } from '@components/pages/Reports/shared/ReportInPlaceExpandContext'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import FamilyOpportunityIntelligenceReport from '@components/pages/Reports/FamilyOpportunityIntelligenceReport.component'
import PatientBaseHealthReport from '@components/pages/Reports/PatientBaseHealthReport.component'
import PatientValueRevenueReport from '@components/pages/Reports/PatientValueRevenueReport.component'
import PatientRiskComplianceReport from '@components/pages/Reports/PatientRiskComplianceReport.component'
import RecallReactivationControlReport from '@components/pages/Reports/RecallReactivationControlReport.component'
import ScheduleReliabilityReport from '@components/pages/Reports/ScheduleReliabilityReport.component'
import ScheduledAppointmentsReport from '@components/pages/Reports/ScheduledAppointmentsReport.component'
import ChairRoomUtilizationReport from '@components/pages/Reports/ChairRoomUtilizationReport.component'
import HygienePerformanceReport from '@components/pages/Reports/HygienePerformanceReport.component'
// TODO: unhide Operational Backlog & Tasks when ready for implementation
// import OperationalBacklogTasksReport from '@components/pages/Reports/OperationalBacklogTasksReport.component'
import ClinicalMixTreatmentProfileReport from '@components/pages/Reports/ClinicalMixTreatmentProfileReport.component'
import CollectionsFinancialEfficiencyReport from '@components/pages/Reports/CollectionsFinancialEfficiencyReport.component'
import ProductionPerformanceReport from '@components/pages/Reports/ProductionPerformanceReport.component'
import TreatmentEffectivenessReport from '@components/pages/Reports/TreatmentEffectivenessReport.component'
import EfficiencyScorecardReport from '@components/pages/Reports/EfficiencyScorecardReport.component'
import ClaimsPerformanceReport from '@components/pages/Reports/ClaimsPerformanceReport.component'
import GoalAttainmentVarianceReport from '@components/pages/Reports/GoalAttainmentVarianceReport.component'
import ReceivablesRiskAnalysisReport from '@components/pages/Reports/ReceivablesRiskAnalysisReport.component'
import RevenueRealizationAnalyticsReport from '@components/pages/Reports/RevenueRealizationAnalyticsReport.component'
import CommunicationCenterPerformanceReport from '@components/pages/Reports/CommunicationCenterPerformanceReport.component'
import MarketingCampaignPerformanceReport from '@components/pages/Reports/MarketingCampaignPerformanceReport.component'
import ReferralReputationReport from '@components/pages/Reports/ReferralReputationReport.component'
import TreatmentConversionFunnelReport from '@components/pages/Reports/TreatmentConversionFunnelReport.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import {
  getFinanceSubmenuLabel,
  getEngagementSubmenuLabel,
  getOperationsSubmenuLabel,
  getPatientsSubmenuLabel,
  getProviderPerformanceSubmenuLabel,
  REPORTS_ENGAGEMENT_SUBMENU,
  REPORTS_FINANCE_SUBMENU,
  REPORTS_OPERATIONS_SUBMENU,
  REPORTS_PATIENTS_SUBMENU,
  REPORTS_PROVIDER_PERFORMANCE_SUBMENU,
} from '@constants/Reports/reportsPage.constants'
import { PATIENT_BASE_HEALTH_FILTERS } from '@constants/Reports/patientBaseHealth.constants'
import type { ReportsOutletContext } from '@pages/Reports/Reports.page'
import ReportsSkeleton from '@pages/Reports/ReportsSkeleton.page'

const BUNDLE_SKELETON_DURATION_MS = 500

function EngagementReportShell(props: { title: string }): React.ReactElement {
  const { title } = props
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)

  return (
    <div className="reports-patient-base-health">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title={title}>
          {reportSaveViewAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={PATIENT_BASE_HEALTH_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>
    </div>
  )
}

const ReportsBundleSubmenuDetail: React.FC = (): React.ReactElement => {
  const navigate = useNavigate()
  const { isPageReady } = useOutletContext<ReportsOutletContext>()
  const { categoryId, reportSlug } = useParams<{
    categoryId: string
    reportSlug: string
  }>()
  const [isBundleSkeleton, setIsBundleSkeleton] = useState(false)
  const [bundleSkeletonKey, setBundleSkeletonKey] = useState(0)

  useEffect(() => {
    if (!isPageReady) return
    setIsBundleSkeleton(true)
    setBundleSkeletonKey((key) => key + 1)
  }, [categoryId, reportSlug, isPageReady])

  useEffect(() => {
    if (!isBundleSkeleton) return
    const timer = window.setTimeout(() => {
      setIsBundleSkeleton(false)
    }, BUNDLE_SKELETON_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [isBundleSkeleton, bundleSkeletonKey])

  const title =
    categoryId === 'patients'
      ? (getPatientsSubmenuLabel(reportSlug) ?? reportSlug)
      : categoryId === 'engagement'
        ? (getEngagementSubmenuLabel(reportSlug) ?? reportSlug)
        : categoryId === 'operations'
          ? (getOperationsSubmenuLabel(reportSlug) ?? reportSlug)
          : categoryId === 'finance'
            ? (getFinanceSubmenuLabel(reportSlug) ?? reportSlug)
            : categoryId === 'provider-performance'
              ? (getProviderPerformanceSubmenuLabel(reportSlug) ?? reportSlug)
              : reportSlug

  const isKnownPatients =
    categoryId === 'patients' &&
    REPORTS_PATIENTS_SUBMENU.some((i) => i.pathSegment === reportSlug)
  const isKnownEngagement =
    categoryId === 'engagement' &&
    REPORTS_ENGAGEMENT_SUBMENU.some((i) => i.pathSegment === reportSlug)
  const isKnownOperations =
    categoryId === 'operations' &&
    REPORTS_OPERATIONS_SUBMENU.some((i) => i.pathSegment === reportSlug)
  const isKnownFinance =
    categoryId === 'finance' &&
    REPORTS_FINANCE_SUBMENU.some((i) => i.pathSegment === reportSlug)
  const isKnownProviderPerformance =
    categoryId === 'provider-performance' &&
    REPORTS_PROVIDER_PERFORMANCE_SUBMENU.some(
      (i) => i.pathSegment === reportSlug,
    )
  const isPatientBaseHealth =
    categoryId === 'patients' && reportSlug === 'patient-base-health'
  const isPatientValueRevenue =
    categoryId === 'patients' && reportSlug === 'patient-value-rev-distribution'
  const isPatientRiskCompliance =
    categoryId === 'patients' && reportSlug === 'patient-risk-compliance'
  const isFamilyOpportunityIntelligence =
    categoryId === 'patients' &&
    reportSlug === 'family-opportunity-intelligence'
  const isRecallReactivationControl =
    categoryId === 'engagement' && reportSlug === 'recall-reactivation-control'
  const isScheduleReliability =
    categoryId === 'engagement' && reportSlug === 'schedule-reliability'
  const isTreatmentConversionFunnel =
    categoryId === 'engagement' && reportSlug === 'treatment-conversion-funnel'
  const isReferralReputation =
    categoryId === 'engagement' && reportSlug === 'referral-reputation'
  const isMarketingCampaignPerformance =
    categoryId === 'engagement' &&
    reportSlug === 'marketing-campaign-performance'
  const isCommunicationCenterPerformance =
    categoryId === 'engagement' &&
    reportSlug === 'communication-center-performance'
  const isScheduledAppointments =
    categoryId === 'operations' && reportSlug === 'scheduled-appointments'
  const isChairRoomUtilization =
    categoryId === 'operations' && reportSlug === 'chair-room-utilization'
  const isHygienePerformance =
    categoryId === 'operations' && reportSlug === 'hygiene-performance'
  // TODO: unhide Operational Backlog & Tasks when ready for implementation
  // const isOperationalBacklogTasks =
  //   categoryId === 'operations' && reportSlug === 'operational-backlog-tasks'
  const isProductionPerformance =
    categoryId === 'provider-performance' &&
    reportSlug === 'production-performance'
  const isCollectionsFinancialEfficiency =
    categoryId === 'provider-performance' &&
    reportSlug === 'collections-financial-efficiency'
  const isClinicalMixTreatmentProfile =
    categoryId === 'provider-performance' &&
    reportSlug === 'clinical-mix-treatment-profile'
  const isTreatmentEffectiveness =
    categoryId === 'provider-performance' &&
    reportSlug === 'treatment-effectiveness'
  const isEfficiencyScorecard =
    categoryId === 'provider-performance' &&
    reportSlug === 'efficiency-scorecard'
  const isRevenueRealizationAnalytics =
    categoryId === 'finance' && reportSlug === 'revenue-realization-analytics'
  const isReceivablesRiskAnalysis =
    categoryId === 'finance' && reportSlug === 'receivables-risk-analysis'
  const isClaimsPerformance =
    categoryId === 'finance' && reportSlug === 'claims-performance'
  const isGoalAttainmentVariance =
    categoryId === 'finance' && reportSlug === 'goal-attainment-variance'
  const engagementReportTitle =
    categoryId === 'engagement' ? getEngagementSubmenuLabel(reportSlug) : null
  const operationsReportTitle =
    categoryId === 'operations' ? getOperationsSubmenuLabel(reportSlug) : null
  const financeReportTitle =
    categoryId === 'finance' ? getFinanceSubmenuLabel(reportSlug) : null
  const providerPerformanceReportTitle =
    categoryId === 'provider-performance'
      ? getProviderPerformanceSubmenuLabel(reportSlug)
      : null

  let bundleReport: React.ReactElement
  if (isPatientBaseHealth) {
    bundleReport = <PatientBaseHealthReport />
  } else if (isPatientValueRevenue) {
    bundleReport = <PatientValueRevenueReport />
  } else if (isPatientRiskCompliance) {
    bundleReport = <PatientRiskComplianceReport />
  } else if (isFamilyOpportunityIntelligence) {
    bundleReport = <FamilyOpportunityIntelligenceReport />
  } else if (isRecallReactivationControl) {
    bundleReport = <RecallReactivationControlReport />
  } else if (isScheduleReliability) {
    bundleReport = <ScheduleReliabilityReport />
  } else if (isTreatmentConversionFunnel) {
    bundleReport = <TreatmentConversionFunnelReport />
  } else if (isReferralReputation) {
    bundleReport = <ReferralReputationReport />
  } else if (isMarketingCampaignPerformance) {
    bundleReport = <MarketingCampaignPerformanceReport />
  } else if (isCommunicationCenterPerformance) {
    bundleReport = <CommunicationCenterPerformanceReport />
  } else if (isScheduledAppointments) {
    bundleReport = <ScheduledAppointmentsReport />
  } else if (isChairRoomUtilization) {
    bundleReport = <ChairRoomUtilizationReport />
  } else if (isHygienePerformance) {
    bundleReport = <HygienePerformanceReport />
    // TODO: unhide Operational Backlog & Tasks when ready for implementation
  } else if (isProductionPerformance) {
    bundleReport = <ProductionPerformanceReport />
  } else if (isCollectionsFinancialEfficiency) {
    bundleReport = <CollectionsFinancialEfficiencyReport />
  } else if (isClinicalMixTreatmentProfile) {
    bundleReport = <ClinicalMixTreatmentProfileReport />
  } else if (isTreatmentEffectiveness) {
    bundleReport = <TreatmentEffectivenessReport />
  } else if (isEfficiencyScorecard) {
    bundleReport = <EfficiencyScorecardReport />
  } else if (isRevenueRealizationAnalytics) {
    bundleReport = <RevenueRealizationAnalyticsReport />
  } else if (isReceivablesRiskAnalysis) {
    bundleReport = <ReceivablesRiskAnalysisReport />
  } else if (isClaimsPerformance) {
    bundleReport = <ClaimsPerformanceReport />
  } else if (isGoalAttainmentVariance) {
    bundleReport = <GoalAttainmentVarianceReport />
  } else if (engagementReportTitle) {
    bundleReport = <EngagementReportShell title={engagementReportTitle} />
  } else if (operationsReportTitle) {
    bundleReport = <EngagementReportShell title={operationsReportTitle} />
  } else if (financeReportTitle) {
    bundleReport = <EngagementReportShell title={financeReportTitle} />
  } else if (providerPerformanceReportTitle) {
    bundleReport = (
      <EngagementReportShell title={providerPerformanceReportTitle} />
    )
  } else {
    bundleReport = (
      <>
        <Button
          type="button"
          variant="text"
          leftIcon={<ArrowLeftIcon aria-hidden />}
          className="reports-section-placeholder__back"
          onClick={() => navigate(`/report/bundles/${categoryId}`)}
        >
          Back to {categoryId === 'patients' ? 'Patients' : categoryId}
        </Button>
        <EmptyState
          title={title}
          description={
            isKnownPatients ||
            isKnownEngagement ||
            isKnownOperations ||
            isKnownFinance ||
            isKnownProviderPerformance
              ? 'Report workspace — connect filters and data grid in a follow-up.'
              : 'This report path is not set up yet.'
          }
        />
      </>
    )
  }

  return (
    <div className="reports-main-panel reports-bundle-submenu-detail">
      {isBundleSkeleton ? (
        <ReportsSkeleton />
      ) : (
        <ReportInPlaceExpandProvider
          key={`${categoryId ?? ''}-${reportSlug ?? ''}`}
        >
          {bundleReport}
        </ReportInPlaceExpandProvider>
      )}
    </div>
  )
}

export default ReportsBundleSubmenuDetail
